import React from 'react';
import { useFocusWithin } from '@mantine/hooks';
import { useOptionalFieldValidation } from '@/components/validation';
import type { UserConfigurationState } from '@/types/userConfiguration';
import { cn } from '@/lib/utils';

export type InvoiceConfigState = Pick<
  UserConfigurationState,
  | 'paymentTermsDays'
  | 'lateFeePercentage'
  | 'lateFeeFixedAmount'
  | 'recurringLateFeeInterval'
  | 'recurringLateFee'
  | 'earlyPaymentDiscountPercentage'
  | 'earlyPaymentFixedAmount'
  | 'earlyPaymentDays'
>;

/** Build an inputProps + errorElement pair from useOptionalFieldValidation */
const buildInputProps = (
  fieldKey: string,
  value: unknown,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  validation: ReturnType<typeof useOptionalFieldValidation>,
): { inputProps: Record<string, unknown>; errorElement: React.ReactNode } => ({
  inputProps: {
    'data-validation-key': fieldKey,
    value: value ?? '',
    onChange,
    onBlur: validation.onBlur,
    className: cn(validation.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  },
  errorElement: validation.errorElement,
});

export const useInvoiceInputs = (
  state: InvoiceConfigState,
  updateInvoiceConfig: (updates: Partial<InvoiceConfigState>) => void,
  localUseLateFee: boolean,
  localLateFeeType: 'percentage' | 'fixed',
  localUseEarlyPaymentDiscount: boolean,
  localEarlyPaymentType: 'percentage' | 'fixed'
) => {
  const paymentTerms = useOptionalFieldValidation('paymentTermsDays');
  const lateFeePct = useOptionalFieldValidation('lateFeePercentage');
  const lateFeeFixed = useOptionalFieldValidation('lateFeeFixedAmount');
  const recurringInterval = useOptionalFieldValidation('recurringLateFeeInterval');
  const earlyDiscountPct = useOptionalFieldValidation('earlyPaymentDiscountPercentage');
  const earlyDiscountFixed = useOptionalFieldValidation('earlyPaymentFixedAmount');
  const earlyDays = useOptionalFieldValidation('earlyPaymentDays');

  const numericOnChange = (updater: (v: number | null) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value ? parseFloat(e.target.value) : null;
    updater(v);
  };

  return {
    paymentTermsInput: buildInputProps(
      'paymentTermsDays',
      state.paymentTermsDays,
      numericOnChange((v) => updateInvoiceConfig({ paymentTermsDays: v ?? undefined })),
      paymentTerms,
    ),
    lateFeePctInput: buildInputProps(
      'lateFeePercentage',
      state.lateFeePercentage,
      numericOnChange((v) => updateInvoiceConfig({ lateFeePercentage: v })),
      lateFeePct,
    ),
    lateFeeFixedInput: buildInputProps(
      'lateFeeFixedAmount',
      state.lateFeeFixedAmount,
      numericOnChange((v) => updateInvoiceConfig({ lateFeeFixedAmount: v })),
      lateFeeFixed,
    ),
    recurringIntervalInput: buildInputProps(
      'recurringLateFeeInterval',
      state.recurringLateFeeInterval,
      numericOnChange((v) => updateInvoiceConfig({ recurringLateFeeInterval: v })),
      recurringInterval,
    ),
    earlyDiscountPctInput: buildInputProps(
      'earlyPaymentDiscountPercentage',
      state.earlyPaymentDiscountPercentage,
      numericOnChange((v) => updateInvoiceConfig({ earlyPaymentDiscountPercentage: v })),
      earlyDiscountPct,
    ),
    earlyDiscountFixedInput: buildInputProps(
      'earlyPaymentFixedAmount',
      state.earlyPaymentFixedAmount,
      numericOnChange((v) => updateInvoiceConfig({ earlyPaymentFixedAmount: v })),
      earlyDiscountFixed,
    ),
    earlyDaysInput: buildInputProps(
      'earlyPaymentDays',
      state.earlyPaymentDays,
      numericOnChange((v) => updateInvoiceConfig({ earlyPaymentDays: v })),
      earlyDays,
    ),
  };
};

// ─── Local validation helpers for per-item rule editors ──────────────────────

type ValidationRule<T> = {
  test: (value: T | null) => boolean;
  message: string;
};

/**
 * Lightweight per-field validation using local state (no provider interaction).
 * Used for self-contained edit forms (cancellation rules, rush premium tiers)
 * where the Zod schema validates the saved data at the form level.
 */
function useLocalValidatedInput<T>({
  value,
  onChange,
  validationKey,
  rules,
  visible,
}: {
  value: T | null;
  onChange: (value: T | null) => void;
  validationKey: string;
  rules: ValidationRule<T>[];
  visible?: boolean;
}) {
  const [touched, setTouched] = React.useState(false);
  const hasEdited = React.useRef(false);

  const error = touched && !rules.every(rule => rule.test(value))
    ? (() => {
        const failedRule = rules.find(rule => !rule.test(value));
        return failedRule ? failedRule.message : null;
      })()
    : null;

  const errorElement = error
    ? React.createElement('p', {
        className: "text-xs text-destructive mt-1 text-left sm:text-right",
        'data-testid': `${validationKey}-error`,
        children: error,
      })
    : null;

  const handleChangeCallback = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    hasEdited.current = true;
    let newValue: T | null = null;
    if (e.target.value) {
      if (e.target.type === 'number' || e.target.inputMode === 'numeric' || e.target.inputMode === 'decimal') {
        const num = parseFloat(e.target.value);
        newValue = (isNaN(num) ? null : num) as unknown as T;
      } else {
        newValue = e.target.value as unknown as T;
      }
    }
    onChange(newValue);
  }, [onChange]);

  const validate = React.useCallback(() => {
    setTouched(true);
    return rules.every(rule => rule.test(value));
  }, [value, rules]);

  const handleBlurCallback = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    /* auto-prepend leading "0" for fractions like ".5" */
    const rawValue = e.currentTarget.value;
    if (/^\.\d+$/.test(rawValue)) {
      const fixed = `0${rawValue}`;
      e.currentTarget.value = fixed;
      onChange(parseFloat(fixed) as unknown as T);
    }

    const related = e.relatedTarget as HTMLElement | null;
    const shouldSkip = related?.dataset.skipValidation === 'true';

    if (!shouldSkip && hasEdited.current) {
      setTouched(true);
    }
  }, [onChange]);

  const reset = React.useCallback(() => {
    setTouched(false);
    hasEdited.current = false;
  }, []);

  React.useEffect(() => {
    if (visible === false) {
      reset();
    }
  }, [visible, reset]);

  const inputProps = React.useMemo(() => ({
    'data-validation-key': validationKey,
    value: value ?? '',
    onChange: handleChangeCallback,
    onBlur: handleBlurCallback,
    className: cn(error && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  }), [validationKey, value, handleChangeCallback, handleBlurCallback, error]);

  return {
    value,
    touched,
    isValid: touched ? rules.every(rule => rule.test(value)) : true,
    error,
    errorElement,
    validate,
    reset,
    handleChange: onChange,
    inputProps,
  };
}

export const useCancellationRuleInputs = (
  rule: { days: number | null; type: 'percentage' | 'fixed'; value: number | null },
  index: number,
  paymentTermsDays: number | null,
  onRuleChange: (updates: Partial<{ days: number | null; type: 'percentage' | 'fixed'; value: number | null }>) => void
) => {
  const { ref: daysRef, focused: daysFocused } = useFocusWithin();
  const { ref: valueRef, focused: valueFocused } = useFocusWithin();
  const [daysTouched, setDaysTouched] = React.useState(false);
  const [valueTouched, setValueTouched] = React.useState(false);

  React.useEffect(() => {
    if (!daysFocused && daysTouched) {
      // Field has lost focus after being touched
    }
  }, [daysFocused, daysTouched]);

  React.useEffect(() => {
    if (!valueFocused && valueTouched) {
      // Field has lost focus after being touched
    }
  }, [valueFocused, valueTouched]);

  const daysInput = useLocalValidatedInput<number>({
    value: rule.days,
    onChange: (v) => onRuleChange({ days: v }),
    validationKey: `cancel.days.${index}`,
    rules: [
      { test: (v) => v !== null && v >= 1, message: 'Days before shift must be at least 1' },
    ],
  });

  const valuePercentageInput = useLocalValidatedInput<number>({
    value: rule.value,
    onChange: (v) => onRuleChange({ value: v }),
    validationKey: `cancel.value.pct.${index}`,
    visible: rule.type === 'percentage',
    rules: [
      {
        test: (v) => v !== null && v >= 0.01 && v <= 100,
        message: 'Penalty must be 0.01-100 %',
      },
    ],
  });

  const valueFixedInput = useLocalValidatedInput<number>({
    value: rule.value,
    onChange: (v) => onRuleChange({ value: v }),
    validationKey: `cancel.value.fixed.${index}`,
    visible: rule.type === 'fixed',
    rules: [
      {
        test: (v) => v !== null && v > 0,
        message: 'Surcharge Amount must be > 0',
      },
    ],
  });

  // Override the inputProps to control when validation errors are shown
  const daysInputWithBlurValidation = {
    ...daysInput,
    errorElement: daysTouched ? daysInput.errorElement : null,
    inputProps: {
      ...daysInput.inputProps,
      ref: daysRef,
      onFocus: () => setDaysTouched(true),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        daysInput.inputProps.onBlur(e);
      }
    }
  };

  const valuePercentageInputWithBlurValidation = {
    ...valuePercentageInput,
    errorElement: valueTouched && rule.type === 'percentage' ? valuePercentageInput.errorElement : null,
    inputProps: {
      ...valuePercentageInput.inputProps,
      ref: valueRef,
      onFocus: () => setValueTouched(true),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        valuePercentageInput.inputProps.onBlur(e);
      }
    }
  };

  const valueFixedInputWithBlurValidation = {
    ...valueFixedInput,
    errorElement: valueTouched && rule.type === 'fixed' ? valueFixedInput.errorElement : null,
    inputProps: {
      ...valueFixedInput.inputProps,
      ref: valueRef,
      onFocus: () => setValueTouched(true),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        valueFixedInput.inputProps.onBlur(e);
      }
    }
  };

  // Reset touched state when rule type changes
  React.useEffect(() => {
    setValueTouched(false);
  }, [rule.type]);

  return {
    daysInput: daysInputWithBlurValidation,
    valuePercentageInput: valuePercentageInputWithBlurValidation,
    valueFixedInput: valueFixedInputWithBlurValidation,
  };
};


export const useRushPremiumRuleInputs = (
  rule: { days: number | null; type: 'percentage' | 'fixed'; value: number | null },
  index: number,
  onRuleChange: (updates: Partial<{ days: number | null; type: 'percentage' | 'fixed'; value: number | null }>) => void
) => {
  const { ref: daysRef, focused: daysFocused } = useFocusWithin();
  const { ref: valueRef, focused: valueFocused } = useFocusWithin();
  const [daysTouched, setDaysTouched] = React.useState(false);
  const [valueTouched, setValueTouched] = React.useState(false);

  React.useEffect(() => {
    if (!daysFocused && daysTouched) {
      // Field lost focus after being touched
    }
  }, [daysFocused, daysTouched]);

  React.useEffect(() => {
    if (!valueFocused && valueTouched) {
      // Field lost focus after being touched
    }
  }, [valueFocused, valueTouched]);

  const daysInput = useLocalValidatedInput<number>({
    value: rule.days,
    onChange: (v) => onRuleChange({ days: v }),
    validationKey: `rush.days.${index}`,
    rules: [
      { test: (v) => v !== null && v >= 1, message: 'Lead time must be at least 1 day' },
    ],
  });

  const valuePercentageInput = useLocalValidatedInput<number>({
    value: rule.value,
    onChange: (v) => onRuleChange({ value: v }),
    validationKey: `rush.value.pct.${index}`,
    visible: rule.type === 'percentage',
    rules: [
      {
        test: (v) => v !== null && v >= 0.01,
        message: 'Premium must be > 0%',
      },
    ],
  });

  const valueFixedInput = useLocalValidatedInput<number>({
    value: rule.value,
    onChange: (v) => onRuleChange({ value: v }),
    validationKey: `rush.value.fixed.${index}`,
    visible: rule.type === 'fixed',
    rules: [
      {
        test: (v) => v !== null && v > 0,
        message: 'Premium Amount must be > 0',
      },
    ],
  });

  const daysInputWithBlurValidation = {
    ...daysInput,
    errorElement: daysTouched ? daysInput.errorElement : null,
    inputProps: {
      ...daysInput.inputProps,
      ref: daysRef,
      onFocus: () => setDaysTouched(true),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        daysInput.inputProps.onBlur(e);
      }
    }
  };

  const valuePercentageInputWithBlurValidation = {
    ...valuePercentageInput,
    errorElement: valueTouched && rule.type === 'percentage' ? valuePercentageInput.errorElement : null,
    inputProps: {
      ...valuePercentageInput.inputProps,
      ref: valueRef,
      onFocus: () => setValueTouched(true),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        valuePercentageInput.inputProps.onBlur(e);
      }
    }
  };

  const valueFixedInputWithBlurValidation = {
    ...valueFixedInput,
    errorElement: valueTouched && rule.type === 'fixed' ? valueFixedInput.errorElement : null,
    inputProps: {
      ...valueFixedInput.inputProps,
      ref: valueRef,
      onFocus: () => setValueTouched(true),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        valueFixedInput.inputProps.onBlur(e);
      }
    }
  };

  React.useEffect(() => {
    setValueTouched(false);
  }, [rule.type]);

  return {
    daysInput: daysInputWithBlurValidation,
    valuePercentageInput: valuePercentageInputWithBlurValidation,
    valueFixedInput: valueFixedInputWithBlurValidation,
  };
};

export const useReminderChipValidation = (
  index: number,
  valueInState: number,
  allReminderDays: ReadonlyArray<number>,
  paymentTerms: number | null,
  updateReminderDayInParentState: (idx: number, newValue: number | null) => void,
  onChipValidationComplete: (idx: number, isValid: boolean, validatedValue: number | null, originalValueBeforeEdit: number) => void
) => {
  const [isBlurredOnce, setIsBlurredOnce] = React.useState(false);
  const initialValueForThisInstanceRef = React.useRef(valueInState);

  React.useEffect(() => {
    initialValueForThisInstanceRef.current = valueInState;
  }, [valueInState]);

  const rules: ValidationRule<number>[] = React.useMemo(() => [
    { test: (v) => v !== null && v >= 1, message: 'Must be >= 1' },
    {
      test: (v) => v !== null && (!paymentTerms || v <= paymentTerms),
      message: 'Reminder days must be <= payment terms'
    },
    {
      test: (v) => {
        if (v === null) return true;
        for (let i = 0; i < allReminderDays.length; i++) {
          if (i === index) continue;
          if (allReminderDays[i] === v) return false;
        }
        return true;
      },
      message: 'Must be unique'
    }
  ], [paymentTerms, allReminderDays, index]);

  const validatedInput = useLocalValidatedInput<number>({
    value: valueInState === 0 ? null : valueInState,
    onChange: (v) => {
      updateReminderDayInParentState(index, v);
    },
    validationKey: `invoice.reminderDays.${index}`,
    rules,
  });

  // Drive the onValidChange callback when validation state settles after blur
  React.useEffect(() => {
    if (isBlurredOnce) {
      const currentValue = valueInState === 0 ? null : valueInState;
      const isValid = rules.every(rule => rule.test(currentValue));
      onChipValidationComplete(index, isValid, currentValue, initialValueForThisInstanceRef.current);
      setIsBlurredOnce(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlurredOnce]);

  // Hide the per-chip error element when the failure reason is
  // "value exceeds Payment Terms" - InvoiceConfig will show a consolidated toast.
  let chipErrorElement = validatedInput.errorElement;
  if (validatedInput.error === 'Reminder days must be <= payment terms') {
    chipErrorElement = null;
  }

  // Effect to re-validate if paymentTerms prop changes
  React.useEffect(() => {
    if (valueInState !== 0 && paymentTerms !== undefined) {
      validatedInput.validate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTerms, valueInState]);


  const handleChipBlur = (rawText: string) => {
    const cleanedText = rawText.trim().replace(/[^\d]/g, "");
    const formattedText = cleanedText === "0" ? "0" : cleanedText.replace(/^0+/, "");
    const numValue = formattedText === "" ? null : Number(formattedText);

    validatedInput.handleChange(numValue);

    const isDuplicate = numValue !== null && allReminderDays.some((day, i) => i !== index && day === numValue);
    if ((initialValueForThisInstanceRef.current === 0 && numValue === null) ||
        numValue === 0 ||
        isDuplicate) {
      onChipValidationComplete(index, false, null, initialValueForThisInstanceRef.current);
      return;
    }

    if (!(initialValueForThisInstanceRef.current === 0 && numValue && numValue > 0)) {
      setIsBlurredOnce(true);
      setTimeout(() => {
        validatedInput.validate();
      }, 0);
    } else {
      onChipValidationComplete(index, true, numValue, initialValueForThisInstanceRef.current);
    }
  };

  return {
    ...validatedInput,
    errorElement: chipErrorElement,
    handleChipBlur,
  };
};
